import React from 'react';
import {
  AnnotationIcon,
  ChatAlt2Icon,
  CollectionIcon,
  DocumentTextIcon,
  DownloadIcon,
  FilmIcon,
  PhotographIcon,
  SparklesIcon,
  MusicNoteIcon,
  UploadIcon,
  VideoCameraIcon,
  ViewGridIcon,
  VolumeUpIcon,
  ZoomInIcon,
} from '@heroicons/react/outline';

import type { DataType } from '@/components/workflows/nodes/BaseNode';
import type { HandleDataType } from '@/lib/workflows/store';

export type HandleDirection = 'source' | 'target';

export interface NodeHandleDef {
  // null = generic/unnamed handle (BaseNode default Handle without an `id`)
  id: string | null;
  type: DataType;
}

export interface NodeTypeMeta {
  type: string;
  defaultLabel: string;
  defaultDescription: string;
  icon: React.ReactNode;
  inputs: NodeHandleDef[];
  outputs: NodeHandleDef[];
  // True for nodes whose default generic input handle accepts any data type
  // (OutputNode uses `<Handle type="target" />` without an id and any sourceType
  // is considered compatible by the store).
  acceptsAnyInput?: boolean;
  // Excluded from the connection-drop menu (e.g. groups are user-created via
  // a selection action, not added directly).
  hiddenFromConnectMenu?: boolean;
}

const iconClass = 'w-5 h-5 text-primary-500';

export const NODE_TYPE_REGISTRY: NodeTypeMeta[] = [
  {
    type: 'input',
    defaultLabel: 'Text Input',
    defaultDescription: 'Add initial text data or prompts',
    icon: React.createElement(DocumentTextIcon, { className: iconClass }),
    inputs: [],
    outputs: [{ id: 'text', type: 'text' }],
  },
  {
    type: 'upload',
    defaultLabel: 'Upload Media',
    defaultDescription: 'Upload images, video, or audio files',
    icon: React.createElement(UploadIcon, { className: iconClass }),
    inputs: [],
    // UploadNode renders outputs conditionally based on its `mode` config
    // (image | video | audio). We list all so the node can satisfy any of
    // those source-type drags.
    outputs: [
      { id: 'image', type: 'image' },
      { id: 'first-frame', type: 'image' },
      { id: 'last-frame', type: 'image' },
      { id: 'video', type: 'video' },
      { id: 'audio', type: 'audio' },
    ],
  },
  {
    type: 'select-image',
    defaultLabel: 'Select Media',
    defaultDescription: 'Select from generated images or videos',
    icon: React.createElement(PhotographIcon, { className: iconClass }),
    inputs: [],
    outputs: [
      { id: 'image', type: 'image' },
      { id: 'first-frame', type: 'image' },
      { id: 'last-frame', type: 'image' },
      { id: 'video', type: 'video' },
    ],
  },
  {
    type: 'image-generation',
    defaultLabel: 'Text to Image',
    defaultDescription: 'Generate images from text prompts',
    icon: React.createElement(SparklesIcon, { className: iconClass }),
    inputs: [{ id: 'prompt', type: 'text' }],
    outputs: [{ id: 'image', type: 'image' }],
  },
  {
    type: 'image-edit',
    defaultLabel: 'Image to Image',
    defaultDescription: 'Transform images with AI',
    icon: React.createElement(FilmIcon, { className: iconClass }),
    inputs: [
      { id: 'image', type: 'image' },
      { id: 'prompt', type: 'text' },
    ],
    outputs: [{ id: 'image', type: 'image' }],
  },
  {
    type: 'image-upscaler',
    defaultLabel: 'Upscale Image',
    defaultDescription: 'Upscale and enhance images with AI',
    icon: React.createElement(ZoomInIcon, { className: iconClass }),
    inputs: [{ id: 'image', type: 'image' }],
    outputs: [{ id: 'image', type: 'image' }],
  },
  {
    type: 'image-splitter',
    defaultLabel: 'Image Splitter',
    defaultDescription: 'Split an image into a grid of smaller images',
    icon: React.createElement(ViewGridIcon, { className: iconClass }),
    inputs: [{ id: 'image', type: 'image' }],
    outputs: [{ id: 'image', type: 'image' }],
  },
  {
    type: 'text-to-video',
    defaultLabel: 'Text to Video',
    defaultDescription: 'Generate videos from text prompts',
    icon: React.createElement(VideoCameraIcon, { className: iconClass }),
    inputs: [{ id: 'prompt', type: 'text' }],
    outputs: [
      { id: 'video', type: 'video' },
      { id: 'first-frame', type: 'image' },
      { id: 'last-frame', type: 'image' },
    ],
  },
  {
    type: 'image-to-video',
    defaultLabel: 'Image to Video',
    defaultDescription: 'Generate videos from images',
    icon: React.createElement(FilmIcon, { className: iconClass }),
    inputs: [
      { id: 'image', type: 'image' },
      { id: 'prompt', type: 'text' },
    ],
    outputs: [
      { id: 'video', type: 'video' },
      { id: 'first-frame', type: 'image' },
      { id: 'last-frame', type: 'image' },
    ],
  },
  {
    type: 'first-last-frame-video',
    defaultLabel: 'First-Last Frame to Video',
    defaultDescription: 'Generate videos from first and last frame',
    icon: React.createElement(VideoCameraIcon, { className: iconClass }),
    inputs: [
      { id: 'first-frame', type: 'image' },
      { id: 'prompt', type: 'text' },
      { id: 'last-frame', type: 'image' },
    ],
    outputs: [
      { id: 'video', type: 'video' },
      { id: 'first-frame', type: 'image' },
      { id: 'last-frame', type: 'image' },
    ],
  },
  {
    type: 'references-to-video',
    defaultLabel: 'References to Video',
    defaultDescription:
      'Generate videos from reference images for consistent characters, objects, or style',
    icon: React.createElement(FilmIcon, { className: iconClass }),
    inputs: [
      { id: 'references', type: 'image' },
      { id: 'prompt', type: 'text' },
    ],
    outputs: [
      { id: 'video', type: 'video' },
      { id: 'first-frame', type: 'image' },
      { id: 'last-frame', type: 'image' },
    ],
  },
  {
    type: 'video-merge',
    defaultLabel: 'Merge Videos',
    defaultDescription: 'Merge multiple videos into one',
    icon: React.createElement(CollectionIcon, { className: iconClass }),
    inputs: [{ id: 'video', type: 'video' }],
    outputs: [{ id: 'video', type: 'video' }],
  },
  {
    type: 'text-generation',
    defaultLabel: 'Text Generation',
    defaultDescription: 'Generate text with AI models',
    icon: React.createElement(ChatAlt2Icon, { className: iconClass }),
    inputs: [
      { id: 'prompt', type: 'text' },
      { id: 'image', type: 'image' },
    ],
    outputs: [{ id: 'text', type: 'text' }],
  },
  {
    type: 'text-preset',
    defaultLabel: 'Text Preset',
    defaultDescription: 'Choose from 300+ preset tools for specific text tasks',
    icon: React.createElement(CollectionIcon, { className: iconClass }),
    inputs: [
      { id: 'text', type: 'text' },
      { id: 'extra1', type: 'text' },
      { id: 'extra2', type: 'text' },
    ],
    outputs: [{ id: 'text', type: 'text' }],
  },
  {
    type: 'text-to-speech',
    defaultLabel: 'Text to Speech',
    defaultDescription: 'Convert text to realistic speech',
    icon: React.createElement(VolumeUpIcon, { className: iconClass }),
    inputs: [{ id: 'text', type: 'text' }],
    outputs: [{ id: 'audio', type: 'audio' }],
  },
  {
    type: 'music-generation',
    defaultLabel: 'Music Generation',
    defaultDescription: 'Generate AI songs with lyrics or instrumental music',
    icon: React.createElement(MusicNoteIcon, { className: iconClass }),
    inputs: [{ id: 'text', type: 'text' }],
    outputs: [{ id: 'audio', type: 'audio' }],
  },
  {
    type: 'talking-video',
    defaultLabel: 'Talking Video',
    defaultDescription:
      'Animate an image or video so it speaks an audio clip or generated voiceover',
    icon: React.createElement(VideoCameraIcon, { className: iconClass }),
    inputs: [
      { id: 'image', type: 'image' },
      { id: 'video', type: 'video' },
      { id: 'audio', type: 'audio' },
      { id: 'text', type: 'text' },
    ],
    outputs: [{ id: 'video', type: 'video' }],
  },
  {
    type: 'output',
    defaultLabel: 'Output',
    defaultDescription: 'Capture and display the final result',
    icon: React.createElement(DownloadIcon, { className: iconClass }),
    // BaseNode default `hasInput` Handle has no `id`; the store treats unknown
    // handles as compatible with anything, so Output accepts every type.
    inputs: [],
    outputs: [],
    acceptsAnyInput: true,
  },
  {
    type: 'note',
    defaultLabel: 'Note',
    defaultDescription: 'Add documentation',
    icon: React.createElement(AnnotationIcon, { className: iconClass }),
    inputs: [],
    outputs: [],
  },
];

export interface CompatibleNodeOption extends NodeTypeMeta {
  // The handle on the new node we should auto-connect to / from.
  // For acceptsAnyInput nodes, this is `{ id: null, type: draggedType }`.
  matchedHandle: NodeHandleDef;
}

/**
 * When the user drags an edge from a node handle and drops it on the canvas,
 * return the list of node types that would form a valid connection. The
 * returned options carry the recommended handle on the new node to attach to.
 *
 * @param draggedType - data type of the handle the user is dragging from. When
 *   'unknown' (e.g. dragging from a generic handle), all nodes that have any
 *   handle on the opposite side are returned — the store treats unknown
 *   handles as compatible with everything.
 * @param draggedHandleType - "source" (user dragged from an output handle, the
 *   new node will provide a matching INPUT) or "target" (user dragged from an
 *   input handle, the new node will provide a matching OUTPUT)
 */
export function getCompatibleNodes(
  draggedType: HandleDataType,
  draggedHandleType: HandleDirection,
): CompatibleNodeOption[] {
  const results: CompatibleNodeOption[] = [];
  const isUnknown = draggedType === 'unknown';
  // For the priority/sort, fall back to text when unknown
  const effectiveType: DataType = isUnknown ? 'text' : draggedType;

  for (const meta of NODE_TYPE_REGISTRY) {
    if (meta.hiddenFromConnectMenu) continue;

    if (draggedHandleType === 'source') {
      // We need a node with a matching INPUT handle
      if (meta.acceptsAnyInput) {
        results.push({
          ...meta,
          matchedHandle: { id: null, type: effectiveType },
        });
        continue;
      }
      if (isUnknown) {
        // Any input is fine — pick the first one if present
        if (meta.inputs.length > 0) {
          results.push({ ...meta, matchedHandle: meta.inputs[0]! });
        }
        continue;
      }
      const match = meta.inputs.find((h) => h.type === draggedType);
      if (match) {
        results.push({ ...meta, matchedHandle: match });
      }
    } else {
      // We need a node with a matching OUTPUT handle
      if (isUnknown) {
        if (meta.outputs.length > 0) {
          results.push({ ...meta, matchedHandle: meta.outputs[0]! });
        }
        continue;
      }
      const match = meta.outputs.find((h) => h.type === draggedType);
      if (match) {
        results.push({ ...meta, matchedHandle: match });
      }
    }
  }

  return sortCompatibleNodes(results, effectiveType, draggedHandleType);
}

// Rank options so the most relevant choice is highlighted first. The ordering
// follows what users actually want to do most of the time per data type.
const PRIORITY_BY_TYPE: Record<
  DataType,
  { source: string[]; target: string[] }
> = {
  text: {
    // Drop text-output onto canvas → most users want to feed an image/video generator
    source: [
      'image-generation',
      'text-generation',
      'text-to-video',
      'text-to-speech',
      'output',
    ],
    // Drop text-input onto canvas → most users want a Text Input or Text Generation source
    target: ['input', 'text-generation', 'text-preset'],
  },
  image: {
    source: [
      'image-edit',
      'image-upscaler',
      'image-to-video',
      'output',
      'text-generation',
      'talking-video',
    ],
    target: ['upload', 'image-generation', 'select-image'],
  },
  video: {
    source: ['video-merge', 'talking-video', 'output'],
    target: [
      'upload',
      'image-to-video',
      'text-to-video',
      'first-last-frame-video',
    ],
  },
  audio: {
    source: ['talking-video', 'output'],
    target: ['upload', 'text-to-speech', 'music-generation'],
  },
};

function sortCompatibleNodes(
  options: CompatibleNodeOption[],
  draggedType: DataType,
  draggedHandleType: HandleDirection,
): CompatibleNodeOption[] {
  const priority = PRIORITY_BY_TYPE[draggedType]?.[draggedHandleType] ?? [];
  const rank = (type: string) => {
    const idx = priority.indexOf(type);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };
  return [...options].sort((a, b) => {
    const ra = rank(a.type);
    const rb = rank(b.type);
    if (ra !== rb) return ra - rb;
    return a.defaultLabel.localeCompare(b.defaultLabel);
  });
}
